/**
 * Max-approvers (PROCURE-2086) — pure helpers for approval resolution.
 * When max_approvers is unset/empty, callers must keep legacy behavior (no cap).
 *
 * Checkbox-required approvers count toward the max. If |required| > max, required may overflow the max.
 * If the capped set still cannot cover the order value *and* someone in the walk can,
 * keep adding people until it can (also past max). If nobody can cover, keep the max cap.
 * Amount-promoted approvers are NOT treated as required for the cap.
 */

export type ApproverLike = {
  lb_user_id?: number | null
  required?: boolean | null
  manually_inserted?: boolean | null
  group_inserted?: boolean | null
  lb_user?: {
    id?: number
    name?: string
    approval_limit?: number | null
    approval_limit_from_group?: number | null
    currency_approval_limit?: string | null
    priority_approval_limit?: number | null
    is_group?: boolean | null
    task_group_users?: Array<{
      lb_user?: {
        id?: number
        name?: string
        approval_limit?: number | null
      } | null
    }> | null
  } | null
}

export type MaxApproverOpts = {
  includeManual?: boolean
  includeGroup?: boolean
  clearing?: boolean
  /** Override required detection (e.g. after order-value promoted `required`). */
  isMandatory?: (approver: ApproverLike) => boolean
}

/** null / undefined / '' / <= 0 → feature off (legacy). Non-integers are floored. */
export function isMaxApproversConfigured(maxApprovers: number | string | null | undefined): boolean {
  if (maxApprovers == null || maxApprovers === '') return false
  const n = Math.floor(Number(maxApprovers))
  return Number.isFinite(n) && n > 0
}

export function normalizeMaxApprovers(maxApprovers: number | string | null | undefined): number | null {
  if (!isMaxApproversConfigured(maxApprovers)) return null
  return Math.floor(Number(maxApprovers))
}

export function anyMaxApproversConfigured(
    ...values: Array<number | string | null | undefined>
): boolean {
  return values.some(isMaxApproversConfigured)
}

export function isGroupApprover(approver: ApproverLike): boolean {
  return !!approver?.lb_user?.is_group || ((approver.lb_user?.task_group_users?.length ?? 0) > 0)
}

/** Matches select-users-groups.getMinApprovalLimitByGroup (lodash minBy). */
export function getMinApprovalLimitByGroup(approver: ApproverLike): number | null {
  const min = (approver.lb_user?.task_group_users ?? [])
      .map((member) => member?.lb_user?.approval_limit)
      .reduce<number | null>((currentMin, limit) => {
        if (limit == null) return currentMin
        const n = Number(limit)
        return currentMin == null || n < currentMin ? n : currentMin
      }, null)
  if (min != null) return min

  const fromGroup = approver.lb_user?.approval_limit_from_group
  if (fromGroup != null) return Number(fromGroup)

  return null
}

function hasThisUserInfiniteLimit(approver: ApproverLike): boolean {
  if (!(approver?.lb_user_id || approver?.lb_user?.id)) return false
  const limit = approver.lb_user?.approval_limit
  if (limit === 0) return false
  return limit == null
}

/** null = unlimited (∞). */
export function getApprovalLimitValue(approver: ApproverLike): number | null {
  if (isGroupApprover(approver)) return getMinApprovalLimitByGroup(approver)
  if (hasThisUserInfiniteLimit(approver)) return null
  const limit = approver?.lb_user?.approval_limit
  if (limit === undefined) return null
  return limit == null ? null : Number(limit)
}

export function hasInfiniteApprovalLimit(approver: ApproverLike): boolean {
  return getApprovalLimitValue(approver) == null
}

export function sortApproversByLimitAsc<T extends ApproverLike>(approvers: T[]): T[] {
  return [...approvers].sort((a, b) => {
    const la = getApprovalLimitValue(a)
    const lb = getApprovalLimitValue(b)
    const aKey = la == null ? Number.MAX_SAFE_INTEGER : la
    const bKey = lb == null ? Number.MAX_SAFE_INTEGER : lb
    if (aKey !== bKey) return aKey - bKey
    // Within same limit: lower priority_approval_limit = higher priority (matches existing UI sort).
    const pa = a.lb_user?.priority_approval_limit ?? -1
    const pb = b.lb_user?.priority_approval_limit ?? -1
    return pa - pb
  })
}

/** Highest approval limit first; within same limit, better priority first. */
export function sortApproversByLimitDescForMaxCap<T extends ApproverLike>(approvers: T[]): T[] {
  return [...approvers].sort((a, b) => {
    const la = getApprovalLimitValue(a)
    const lb = getApprovalLimitValue(b)
    if (la == null && lb == null) {
      return (a.lb_user?.priority_approval_limit ?? -1) - (b.lb_user?.priority_approval_limit ?? -1)
    }
    if (la == null) return -1
    if (lb == null) return 1
    if (lb !== la) return lb - la
    return (a.lb_user?.priority_approval_limit ?? -1) - (b.lb_user?.priority_approval_limit ?? -1)
  })
}

export function isMandatoryApprover(approver: ApproverLike, opts?: MaxApproverOpts): boolean {
  if (opts?.isMandatory) return opts.isMandatory(approver)
  if (approver.required) return true
  if (opts?.includeManual && approver.manually_inserted) return true
  if (opts?.includeGroup && approver.group_inserted && !opts?.clearing) return true
  return false
}

/** Sync mirror of markAsRequiredWhenApprovalLimitLowerOrEqualThanMandatory (per entity list). */
export function cascadeRequiredByApprovalLimit<T extends ApproverLike>(approvers: T[]): T[] {
  const list = approvers.map((a) => ({...a}))
  const required = list.filter((a) => !!a.required)
  if (required.length === 0) return list

  if (required.some(hasInfiniteApprovalLimit)) {
    list.forEach((a) => {
      a.required = true
    })
    return list
  }

  let max: number | null = null
  for (const r of required) {
    const lim = getApprovalLimitValue(r)
    if (lim != null && (max == null || lim > max)) max = lim
  }
  if (max == null) return list

  list.forEach((a) => {
    if (hasInfiniteApprovalLimit(a)) return
    const lim = getApprovalLimitValue(a)
    if (lim != null && lim <= max!) a.required = true
  })
  return list
}

/**
 * If the capped set cannot cover `amount`, keep adding remaining people from `pool`
 * (limit ASC, same as the Bestellwert walk) until someone covers it — even past max.
 * Only overflows when the pool can actually cover; otherwise keep the max cap
 * (adding more people who still cannot cover would only break max for no gain).
 */
function overflowUntilAmountCovered<T extends ApproverLike>(
    selected: T[],
    pool: T[],
    amount: number
): T[] {
  if (!Number.isFinite(amount) || amount < 0 || setCoversAmount(selected, amount)) {
    return selected
  }
  // Nobody in the walk can cover → respect max (do not dump the whole list).
  if (!setCoversAmount(pool, amount)) return selected

  const selectedIds = new Set(selected.map((a) => a.lb_user_id).filter((id) => id != null))
  const remaining = sortApproversByLimitAsc(
      pool.filter((a) => a.lb_user_id != null && !selectedIds.has(a.lb_user_id))
  )
  const list = [...selected]
  for (const approver of remaining) {
    list.push({...approver} as T)
    if (personCoversAmount(approver, amount) || setCoversAmount(list, amount)) break
  }
  return sortApproversByLimitAsc(list)
}

function capThenCoverAmount<T extends ApproverLike>(
    adequate: T[],
    maxApprovers: number | string | null | undefined,
    amount: number,
    opts?: MaxApproverOpts
): T[] {
  return overflowUntilAmountCovered(
      applyMaxApprovers(adequate, maxApprovers, opts),
      adequate,
      amount
  )
}

/**
 * Keep required approvers (always), fill remaining slots with highest-limit optionals.
 * Total stays ≤ max unless required approvers overflow.
 * Tie-break among equal limits: better priority_approval_limit (lower number).
 */
export function applyMaxApprovers<T extends ApproverLike>(
    approvers: T[],
    maxApprovers: number | string | null | undefined,
    opts?: MaxApproverOpts
): T[] {
  const maxN = normalizeMaxApprovers(maxApprovers)
  if (maxN == null) return approvers

  const mandatory = approvers.filter((a) => isMandatoryApprover(a, opts))
  const optional = sortApproversByLimitDescForMaxCap(
      approvers.filter((a) => !isMandatoryApprover(a, opts))
  )

  const slotsLeft = maxN - mandatory.length
  if (slotsLeft <= 0) return sortApproversByLimitAsc(mandatory)

  const top = optional.slice(0, slotsLeft)
  return sortApproversByLimitAsc([...mandatory, ...top])
}

function setCoversAmount(approvers: ApproverLike[], amount: number): boolean {
  if (approvers.some(hasInfiniteApprovalLimit)) return true
  let max = 0
  for (const a of approvers) {
    const limit = getApprovalLimitValue(a)
    if (limit != null && limit > max) max = limit
  }
  return max >= amount
}

function personCoversAmount(approver: ApproverLike, amount: number): boolean {
  if (hasInfiniteApprovalLimit(approver)) return true
  const limit = getApprovalLimitValue(approver)
  return limit != null && limit >= amount
}

/**
 * Sync mirror of getApproversConsideringLimit (no currency conversion).
 * Mutates copies only — does not alter the input array objects.
 */
export function getApproversConsideringLimitSync<T extends ApproverLike>(
    approvers: T[],
    amount: number,
    opts?: MaxApproverOpts
): T[] {
  const required = approvers.filter((a) => isMandatoryApprover(a, opts))
  const optional = sortApproversByLimitAsc(
      approvers.filter((a) => !isMandatoryApprover(a, opts))
  )
  const list: T[] = required.map((a) => ({...a}))

  if (setCoversAmount(list, amount)) return sortApproversByLimitAsc(list)

  for (const approver of optional) {
    list.push({...approver, required: true} as T)
    if (personCoversAmount(approver, amount)) break
  }
  return sortApproversByLimitAsc(list)
}

/**
 * Per-entity evaluation when max is set:
 * order-value filter (uses required flags as-is, incl. cascade) → then max N.
 * Max-cap required = `opts.isMandatory` if provided, else required flags *before* the walk
 * (so amount-promoted required does not consume max slots).
 * When max unset, returns the list unchanged (caller keeps legacy global trim).
 */
export function resolveEntityApproversForMax<T extends ApproverLike>(
    approvers: T[],
    maxApprovers: number | string | null | undefined,
    amount: number,
    opts?: MaxApproverOpts
): T[] {
  if (!isMaxApproversConfigured(maxApprovers)) return approvers

  const mandatoryIds = opts?.isMandatory
      ? null
      : new Set(
          approvers
              .filter((a) => !!a.required)
              .map((a) => a.lb_user_id)
              .filter((id) => id != null)
      )

  // Order-value walk must see cascade-promoted required flags.
  const adequate = getApproversConsideringLimitSync(approvers, amount)
  return capThenCoverAmount(adequate, maxApprovers, amount, {
    isMandatory: opts?.isMandatory
        ?? ((a) => mandatoryIds!.has(a.lb_user_id)),
  })
}

export function previewApproversForOrderValue<T extends ApproverLike>(
    approvers: T[],
    maxApprovers: number | string | null | undefined,
    orderValue: number | null | undefined
): T[] {
  const valid = approvers.filter((a) => a.lb_user_id != null && Number.isFinite(Number(a.lb_user_id)))
  // Only checkbox-required counts toward max — not cascade-promoted required flags.
  const originalRequiredIds = new Set(
      valid.filter((a) => !!a.required).map((a) => a.lb_user_id).filter((id) => id != null)
  )
  const requiredOpts: MaxApproverOpts = {
    isMandatory: (a) => originalRequiredIds.has(a.lb_user_id),
  }
  const withCascade = cascadeRequiredByApprovalLimit(valid)

  if (orderValue == null || !Number.isFinite(Number(orderValue)) || Number(orderValue) < 0) {
    return isMaxApproversConfigured(maxApprovers)
        ? applyMaxApprovers(withCascade, maxApprovers, requiredOpts)
        : sortApproversByLimitAsc(withCascade)
  }

  const adequate = getApproversConsideringLimitSync(withCascade, Number(orderValue))
  if (!isMaxApproversConfigured(maxApprovers)) {
    return sortApproversByLimitAsc(adequate)
  }
  return capThenCoverAmount(adequate, maxApprovers, Number(orderValue), requiredOpts)
}

/**
 * After async order-value trim: cap using original checkbox-required from `originalRequiredSnapshot`
 * (not cascade-/amount-promoted `required` flags).
 * Pass `originalRequiredSnapshot` as the list *before* required cascade and order-value trim.
 */
export function capApproversAfterLimitCheck<T extends ApproverLike>(
    originalRequiredSnapshot: T[],
    adequateAfterLimit: T[],
    maxApprovers: number | string | null | undefined,
    amount?: number | null
): T[] {
  if (!isMaxApproversConfigured(maxApprovers)) return adequateAfterLimit
  const mandatoryIds = new Set(
      originalRequiredSnapshot
          .filter((a) => !!a.required)
          .map((a) => a.lb_user_id)
          .filter((id) => id != null)
  )
  const opts: MaxApproverOpts = {
    isMandatory: (a) => mandatoryIds.has(a.lb_user_id),
  }
  if (amount == null || !Number.isFinite(Number(amount))) {
    return applyMaxApprovers(adequateAfterLimit, maxApprovers, opts)
  }
  return capThenCoverAmount(adequateAfterLimit, maxApprovers, Number(amount), opts)
}
